import { useState } from 'react'
import { Calendar, X } from 'lucide-react'
import { cn } from '@utils/cn'

interface SchedulingModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onConfirm: (scheduledAt: string) => void
  readonly isLoading?: boolean
}

export function SchedulingModal({ open, onClose, onConfirm, isLoading }: SchedulingModalProps) {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(today)
  const [time, setTime] = useState('09:00')

  if (!open) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!date || !time) return
    const scheduled = new Date(`${date}T${time}:00`)
    if (Number.isNaN(scheduled.getTime()) || scheduled.getTime() <= Date.now()) {
      return
    }
    onConfirm(`${date}T${time}:00`)
  }

  const isPastSelection = (() => {
    if (!date || !time) return false
    const scheduled = new Date(`${date}T${time}:00`)
    return !Number.isNaN(scheduled.getTime()) && scheduled.getTime() <= Date.now()
  })()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 md:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scheduling-modal-title"
    >
      <div className="w-full max-w-[calc(100vw-1.5rem)] rounded-xl bg-white shadow-xl md:max-w-md" onClick={(e) => e.stopPropagation()} role="document">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4 md:px-5">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h2 id="scheduling-modal-title" className="text-lg font-semibold text-gray-900">
              Fazer marcação
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-4 md:p-5">
          <p className="text-sm text-gray-600">
            Indique o dia e a hora em que o cliente virá à oficina. Depois preencha nome do cliente e marca do veículo no chat.
          </p>
          <div>
            <label htmlFor="scheduling-date" className="mb-1 block text-sm font-medium text-gray-700">
              Data
            </label>
            <input
              id="scheduling-date"
              type="date"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full min-w-0 max-w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="scheduling-time" className="mb-1 block text-sm font-medium text-gray-700">
              Hora
            </label>
            <input
              id="scheduling-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
              className="w-full min-w-0 max-w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          {isPastSelection ? (
            <p className="text-sm text-red-600" role="alert">
              Escolha uma data e hora no futuro.
            </p>
          ) : null}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:w-auto"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || isPastSelection}
              className={cn(
                'inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:w-auto',
                isLoading && 'opacity-50'
              )}
            >
              <Calendar className="h-4 w-4" />
              Iniciar marcação
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
